/**
 * La sessione di turno: **una sola fonte**, leggibile anche fuori da React.
 *
 * Prima stava in un `useState` dentro `FornitoreSessione`, e chi ne aveva
 * bisogno fuori dall'albero React se la rileggeva a mano: `apps/web/src/rotte.tsx`
 * apriva `localStorage`, faceva `JSON.parse` in un `try/catch` e frugava dentro
 * `utente.ruoli` — perché `beforeLoad` di TanStack Router gira **prima** che
 * esista un componente da cui leggere un contesto. Erano due copie della stessa
 * cosa: la chiave, la forma del dato e il modo di tollerare un JSON rotto,
 * scritti in due file che non si conoscevano.
 *
 * Con lo store la lettura fuori da React è `statoSessione.getState().identita`,
 * e la chiave sta in un posto solo.
 *
 * ## Il formato salvato non cambia
 *
 * `persist` di zustand scriverebbe `{"state":{...},"version":0}`. Qui il
 * deposito è scritto a mano apposta per continuare a salvare **l'identità
 * nuda**, come si è sempre fatto. Non è nostalgia: quella chiave è già nei
 * browser delle postazioni, e un formato nuovo vorrebbe dire buttare fuori
 * tutti al primo aggiornamento — a inizio turno, con il reparto che aspetta.
 *
 * ## Le due applicazioni si accorgono l'una dell'altra
 *
 * Banco e ufficio stanno sulla stessa origine (`/` e `/backoffice/`) e
 * condividono `localStorage` **di proposito**: sono due viste sullo stesso
 * turno. Finora «Esci» dall'una spegneva l'altra solo al primo ricaricamento.
 * L'evento `storage` — che il browser manda alle *altre* schede, mai a quella
 * che ha scritto — fa rileggere lo store, e la seconda scheda torna all'accesso
 * da sé.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
/** La stessa chiave di sempre, e adesso l'unico posto in cui è scritta. */
export const CHIAVE_SESSIONE = 'errebi:sessione';
/**
 * Il deposito: legge e scrive l'identità nuda sotto `CHIAVE_SESSIONE`.
 *
 * Tollera un contenuto illeggibile tornando `null` — una chiave manomessa o
 * scritta da una versione futura non deve impedire di **entrare**, che è la
 * cosa che l'operatore può sempre rifare.
 */
const deposito = {
    getItem: (nome) => {
        try {
            const grezza = localStorage.getItem(nome);
            return grezza ? { state: { identita: JSON.parse(grezza) } } : null;
        }
        catch {
            return null;
        }
    },
    setItem: (nome, valore) => {
        const identita = valore.state.identita;
        if (identita)
            localStorage.setItem(nome, JSON.stringify(identita));
        else
            localStorage.removeItem(nome);
    },
    removeItem: (nome) => {
        localStorage.removeItem(nome);
    },
};
export const statoSessione = create()(persist((set) => ({
    identita: null,
    salva: (identita) => set({ identita }),
    esci: () => set({ identita: null }),
}), {
    name: CHIAVE_SESSIONE,
    storage: deposito,
    partialize: (stato) => ({ identita: stato.identita }),
}));
/**
 * L'identità **adesso**, senza passare da React.
 *
 * La usa `beforeLoad` del router, che decide dove mandare chi apre `/` prima
 * che esista un componente: da lì un contesto non si legge.
 */
export function identitaCorrente() {
    return statoSessione.getState().identita;
}
/** I ruoli di chi è dentro, o l'elenco vuoto. La domanda che fa il router. */
export function ruoliCorrenti() {
    return identitaCorrente()?.utente.ruoli ?? [];
}
/**
 * Rilegge la sessione da `localStorage`, **assenza compresa**.
 *
 * Serve a due mestieri diversi e per la stessa ragione — lo store è un
 * singleton che si idrata una volta sola, all'import:
 *
 * - **l'altra scheda** ha fatto accesso o è uscita (vedi il listener qui
 *   sotto);
 * - **i test** che preparano una sessione scrivendo la chiave a mano, come
 *   farebbe un browser che riapre la postazione. Senza questa chiamata
 *   scriverebbero in una memoria che nessuno rilegge più.
 *
 * Non è `persist.rehydrate()`, ed è deliberato: quello, davanti a una chiave
 * **sparita**, non fa niente — lo store resta con l'identità di prima. Cioè
 * proprio il caso che conta, che è l'uscita fatta dall'altra scheda.
 */
export function ricaricaSessione() {
    const salvata = deposito.getItem(CHIAVE_SESSIONE);
    const stato = salvata instanceof Promise ? null : salvata;
    statoSessione.setState({ identita: stato?.state.identita ?? null });
}
/** Dimentica tutto, memoria compresa. Nei test, fra una prova e l'altra. */
export function azzeraSessione() {
    statoSessione.setState({ identita: null });
    localStorage.removeItem(CHIAVE_SESSIONE);
}
// L'altra scheda ha fatto accesso o è uscita: qui si rilegge. `storage` arriva
// **solo** alle altre schede, quindi non c'è nessun anello con la scrittura
// appena fatta da questa.
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (evento) => {
        if (evento.key === CHIAVE_SESSIONE)
            ricaricaSessione();
    });
}
//# sourceMappingURL=state.js.map